// jshint ignore:start

import { useState } from 'react'
import { FileChecksum } from 'activestorage/src/file_checksum'
import { BlobUpload } from 'activestorage/src/blob_upload'
import axios from 'axios'

function createDirectUpload(params, retriesLeft = 3) {
  return new Promise((resolve, reject) => {
    axios
      .post('/api/internal/direct-uploads/item-images', { blob: params })
      .then((result) => { resolve(result) })
      .catch((error) => {
        const status = error.response && error.response.status

        if (retriesLeft > 0 && status === 500) {
          setTimeout(() => {
            createDirectUpload(params, retriesLeft - 1).then(resolve, reject)
          }, 500)
        } else {
          reject(error)
        }
      })
  })
}

function calculateChecksum(file) {
  return new Promise((resolve, reject) => {
    FileChecksum.create(file, (error, checksum) => {
      if (error) {
        return reject(error)
      }
      resolve(checksum)
    })
  })
}

function getFileMetadata(file) {
  return new Promise((resolve) => {
    calculateChecksum(file)
      .then((checksum) => {
        resolve({
          checksum,
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          byte_size: file.size,
        })
      })
  })
}

function upload(url, headers, file, handleProgress) {
  const upload = new BlobUpload({ file, directUploadData: { url, headers } })
  upload.xhr.upload.addEventListener('progress', e => handleProgress(Math.round((e.loaded * 100) / e.total)))
  return new Promise((resolve, reject) => {
    upload.create(error => {
      if (error) {
        reject(error)
      } else {
        resolve()
      }
    })
  })
}

export default function useDirectUpload() {
  const [signedBlobId, setSignedBlobId] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)

  function handleProgress(progress) {
    setProgress(progress)
  }

  function directUpload(file, opts = {}) {
    setProgress(0)
    return getFileMetadata(file).then((data) => {
      setIsUploading(true)
      createDirectUpload(data)
        .then((result) => {
          const { headers, signedBlobId, url } = result.data.data.attributes

          upload(url, headers, file, (progress) => {
            handleProgress(progress)
            if (opts.onProgress) {
              opts.onProgress(progress)
            }
          }).then(() => {
            setSignedBlobId(signedBlobId)
            setIsUploading(false)
            if (opts.onCompleted) {
              opts.onCompleted(signedBlobId)
            }
          })
        })
    })
  }
  return [isUploading, signedBlobId, directUpload, progress]
}

export function useMultiDirectUpload({
  onCompleted,
  onProgress,
  returnWithFile,
}) {
  const [isUploading, setIsUploading] = useState(false)
  const [,, directUpload] = useDirectUpload()

  function upload(filesToUpload) {
    setIsUploading(true)
    const blobIds = []
    let totalLoaded = 0
    let totalSize = 0
    const files = Array.from(filesToUpload)
    files.forEach(file => {
      totalSize += file.size
      directUpload(file, {
        onCompleted: (blobId) => {
          blobIds.push(returnWithFile ? { blobId, file } : blobId)

          if (blobIds.length === files.length) {
            onCompleted(blobIds)
          }
        },
        onProgress: (progress) => {
          totalLoaded += file.size * progress
          onProgress(totalLoaded, totalSize)
          if (totalLoaded === totalSize) {
            setIsUploading(false)
          }
        },
      })
    })
  }

  return { upload, isUploading }
}
